import type { AnimationConfig, AnimationDirection } from './types';
import { AnimationObserver } from './observer';
import { Animator } from './animator';

export class ScrollAnimations {
  private observer: AnimationObserver;
  private defaultConfig: AnimationConfig = {
    rootMargin: '0px 0px -20% 0px', // Start animation when 20% visible
    threshold: 0.075, // Need 10% of element visible
    staggerDelay: 150, // Slower stagger
    defaultDuration: 800 // Slower animation
  };

  constructor(config: AnimationConfig = {}) {
    this.defaultConfig = { ...this.defaultConfig, ...config };
    this.observer = new AnimationObserver(
      this.defaultConfig,
      this.handleIntersection.bind(this)
    );
    this.init();
  }

  private init(): void {
    this.observeElements('[data-animate]');
    this.observeElements('[data-animate-group]');
    this.setupMutationObserver();
  }

  private observeElements(selector: string): void {
    document.querySelectorAll(selector).forEach(element => {
      // Don't add initial state to group containers
      if (!element.hasAttribute('data-animate-group')) {
        Animator.prepareElement(element);
      }
      this.observer.observe(element);
    });
  }

  private handleIntersection(element: Element): void {
    if (element.hasAttribute('data-animate-group')) {
      this.animateGroup(element);
    } else if (element.hasAttribute('data-animate-stagger-row')) {
      this.animateWithRowStagger(element);
    } else {
      this.animateSingle(element);
    }
  }

  private animateSingle(element: Element): void {
    const animationType = element.getAttribute('data-animate') || 'fade-in';
    Animator.animateElement(element, animationType);
    this.observer.unobserve(element);
  }

  private animateWithRowStagger(element: Element): void {
    const animationType = element.getAttribute('data-animate') || 'fade-in';
    const baseDelay = parseInt(element.getAttribute('data-stagger-delay') || '150');

    // Get element's position
    const rect = element.getBoundingClientRect();
    const parent = element.parentElement;

    if (!parent) {
      this.animateSingle(element);
      return;
    }

    // Find siblings in the same row (within 10px of same Y position)
    const siblings = Array.from(parent.children).filter(child => {
      const childRect = child.getBoundingClientRect();
      return Math.abs(childRect.top - rect.top) < 10 &&
        child.hasAttribute('data-animate-stagger-row') &&
        !this.observer.animatedElements.has(child);
    });

    // Sort by X position and animate
    siblings.sort((a, b) => a.getBoundingClientRect().left - b.getBoundingClientRect().left);

    siblings.forEach((sibling, index) => {
      Animator.animateElement(sibling, animationType, index * baseDelay);
      this.observer.unobserve(sibling);
    });
  }

  private animateGroup(groupElement: Element): void {
    const animationType = groupElement.getAttribute('data-animate-group') || 'fade-in';
    const staggerDelay = parseInt(
      groupElement.getAttribute('data-animate-stagger') ||
      String(this.defaultConfig.staggerDelay)
    );
    const childSelector = groupElement.getAttribute('data-animate-children') || ':scope > *';

    const children = groupElement.querySelectorAll(childSelector);

    // On mobile (< 768px), just observe children without complex stagger
    if (window.innerWidth < 768) {
      Array.from(children).forEach((child, index) => {
        Animator.prepareElement(child);
        child.setAttribute('data-animate', animationType);
        child.setAttribute('data-animate-delay', String(index * 100));
        this.observer.observe(child);
      });
    } else {
      // Desktop: prepare children with row-based stagger
      Array.from(children).forEach((child) => {
        Animator.prepareElement(child);
        child.setAttribute('data-animate', animationType);
        child.setAttribute('data-animate-stagger-row', 'true');
        child.setAttribute('data-stagger-delay', String(staggerDelay));
        this.observer.observe(child);
      });
    }

    this.observer.unobserve(groupElement);
  }

  private setupMutationObserver(): void {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node instanceof Element) {
            this.checkNewElement(node);
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  private checkNewElement(element: Element): void {
    if (element.hasAttribute('data-animate') || element.hasAttribute('data-animate-group')) {
      if (!this.observer.animatedElements.has(element)) {
        Animator.prepareElement(element);
        this.observer.observe(element);
      }
    }

    element.querySelectorAll('[data-animate], [data-animate-group]').forEach(child => {
      if (!this.observer.animatedElements.has(child)) {
        Animator.prepareElement(child);
        this.observer.observe(child);
      }
    });
  }

  destroy(): void {
    this.observer.disconnect();
  }
}

// Export convenience function
export function initScrollAnimations(config?: AnimationConfig): ScrollAnimations {
  return new ScrollAnimations(config);
}
